import * as React from "react";
import base64     from "base-64";

import {XdSentry}      from "@Common/libs/sentry/xd-sentry";
import {makeChain,
        ComponentLink,
        Link}          from "@Common/react/cst2";
import {UseState}      from "@Common/react/use-state";
import {Types}         from "@Common/types";
import {isDevXd}       from "@Common/ui-components/debug/is-dev";
import {UploadButton}  from "@Common/ui-components/upload-button/upload-button";

// TODO: Remove reliance on template_admin
import {apis as ccApis,
        createCCApis}           from "../../../../../../../template_admin/client/src/api/apis";
import {createTemplateLocation} from "../../../../../../../template_admin/client/src/routes/setup-new-template/template-upload/utils/utils";
import * as Constants           from "../../../../../../../template_admin/client/src/common/constants";


export const TemplateUploadButton = ({
    onUploadComplete,
    onUploadError,
}:{
    onUploadComplete  : (idmlData:any) => void,
    onUploadError     : (error:string) => void,
}) => (

    makeChain<JSX.Element>()

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        uploadProgressPercent  : number,
    }>>) => UseState({
        defaultValue: {
            uploadProgressPercent: 0,
        },
        next,
    }))

    // build the upload method
    .then("onUpload", ({state}, next:Link<JSX.Element, any>) => next(async (event) => {
        const files = event.target.files;
        if (files.length !== 1) return onUploadError("File not found.");
        const file = files[0];

        // show processing status in button
        state.onChange({
            ...state.value,
            uploadProgressPercent: -1
        });

        // create the template location
        const systemConstants = await ccApis.getSystemConstants();
        const host = systemConstants.DS_SERVER_URL;
        let templateObj = await createTemplateLocation({}, createCCApis(host), true);

        // upload the file
        let headers = {
            'filename': file.name,
            'templatepath': templateObj.filePath,
            'id': templateObj._id,
            "connection": "keep-alive",
            "authorization": `Basic ${base64.encode(`${Constants.CC_API_USERNAME}:${Constants.CC_API_PASSWORD}`)}`
        };

        let formData = new FormData();
        formData.append("file", file);

        await new Promise((resolve, reject) => {

            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${host}${Constants.CC_TEMPLATE_UPLOAD_FILES_URL}`, true);

            for (const [key, value] of Object.entries(headers)) {
                xhr.setRequestHeader(key, value);
            }

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    state.onChange({
                        ...state.value,
                        uploadProgressPercent: (event.loaded / event.total) * 100
                    });
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(xhr.response);
                } else {
                    reject(`File couldn't be sent! ${xhr.statusText}`);
                }
            };

            xhr.onerror = () => {
                reject(`File couldn't be sent! ${xhr.statusText}`);
            };

            xhr.send(formData);
        }).catch((error) => {
            if (isDevXd()) console.error(`TemplateUploadButton Idml upload failed: ${error}`);
            XdSentry.addBreadcrumb({
                category: "idmlupload",
                message: "IDML upload failed.",
                level: XdSentry.Severity.Error,
                data: {
                    error,
                    requestHeaders: headers,
                }
            });
            XdSentry.captureMessage("IDML Upload Link File Error", XdSentry.Severity.Log);
            return onUploadError(`Error uploading ${file.name}.`);
        });

        // get IDML info
        const filePath = templateObj.filePath + "/" + templateObj._id;
        const response = await fetch(`${host}${Constants.CC_GET_IDML_ATTRIBUTES_URL}?localPath=${encodeURIComponent(filePath)}.idml`, {
            headers: {
                filename: file.name,
                id: templateObj._id,
                connection: "keep-alive",
                authorization: `Basic ${base64.encode(`${Constants.CC_API_USERNAME}:${Constants.CC_API_PASSWORD}`)}`
            },
            method: "GET"
        }).catch((error) => {
            if (isDevXd()) console.error(`TemplateUploadButton Idml Info request failed: ${error}`);
            XdSentry.addBreadcrumb({
                category: "idmlupload",
                message: "IDML upload info request failed.",
                level: XdSentry.Severity.Error,
                data: {
                    error,
                    requestHeaders: headers,
                }
            });
            XdSentry.captureMessage("IDML Upload Error", XdSentry.Severity.Log);
            return onUploadError(`Error processing ${file.name}.`);
        });

        if (!response) {
            if (isDevXd()) console.error(`TemplateUploadButton Idml Info request returned no response.`);
            XdSentry.addBreadcrumb({
                category: "idmlupload",
                message: "IDML upload info response was empty.",
                level: XdSentry.Severity.Error,
                data: {
                    requestHeaders: headers,
                }
            });
            XdSentry.captureMessage("IDML Upload Error", XdSentry.Severity.Log);
            return onUploadError(`Error processing ${file.name}.`);
        }

        const idmlInfo = await response.json();
        if (idmlInfo.error) {
            if (isDevXd()) console.error(`TemplateUploadButton Idml Info contains error: ${idmlInfo.error}`);
            XdSentry.addBreadcrumb({
                category: "idmlupload",
                message: "IDML upload info response contained error.",
                level: XdSentry.Severity.Error,
                data: {
                    error: idmlInfo.error,
                    requestHeaders: headers,
                }
            });
            XdSentry.captureMessage("IDML Upload Error", XdSentry.Severity.Log);
            return onUploadError(idmlInfo.error);
        }

        // add the file name and path to the IDML info and make callback
        idmlInfo.fileName = file.name;
        idmlInfo.filePath = filePath;
        idmlInfo.folderPath = templateObj.filePath;
        idmlInfo.pageSize.width = +parseFloat(idmlInfo?.pageSize?.width ?? 0).toFixed(3);
        idmlInfo.pageSize.height = +parseFloat(idmlInfo?.pageSize?.height ?? 0).toFixed(3);

        // run callback
        onUploadComplete(idmlInfo);

        // reset progress
        state.onChange({
            ...state.value,
            uploadProgressPercent: 0
        });

    }))

    // render the component
    .then(({state, onUpload}) => (
        <UploadButton
            buttonText="Upload Template"
            onUpload={onUpload}
            uploadProgressPercent={state.value.uploadProgressPercent}
            acceptedTypes=".idml"
        />
    ))

    .run()

);
